import React, { useState } from 'react';
import { Bell, X, AlertTriangle, Info, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useNotifications, SystemReminder, NotificationType } from '@/context/NotificationContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const getTypeIcon = (type: NotificationType) => {
  const iconClass = "h-4 w-4";
  switch (type) {
    case 'warning':
      return <AlertTriangle className={`${iconClass} text-yellow-500`} />;
    case 'error':
      return <AlertCircle className={`${iconClass} text-red-500`} />;
    case 'info':
      return <Info className={`${iconClass} text-blue-500`} />;
    case 'success':
      return <CheckCircle className={`${iconClass} text-green-500`} />;
    default:
      return <Info className={`${iconClass} text-gray-500`} />;
  }
};

const getTypeStyles = (type: NotificationType) => {
  switch (type) {
    case 'warning':
      return 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100';
    case 'error':
      return 'border-red-200 bg-red-50 hover:bg-red-100';
    case 'info':
      return 'border-blue-200 bg-blue-50 hover:bg-blue-100';
    case 'success':
      return 'border-green-200 bg-green-50 hover:bg-green-100';
    default:
      return 'border-gray-200 bg-gray-50 hover:bg-gray-100';
  }
};

const formatTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Hace ${diffHours}h`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays}d`;
};

interface NotificationItemProps {
  reminder: SystemReminder;
  onMarkAsRead: (id: string) => void;
  onClear: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ reminder, onMarkAsRead, onClear }) => {
  const handleClick = () => {
    if (!reminder.isRead) {
      onMarkAsRead(reminder.id);
    }
    if (reminder.action?.onClick) {
      reminder.action.onClick();
    }
  };

  return (
    <div className={`relative p-3 border rounded-lg transition-colors ${getTypeStyles(reminder.type)} ${
      !reminder.isRead ? 'ring-2 ring-offset-2 ring-blue-200' : ''
    }`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          {getTypeIcon(reminder.type)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-medium ${!reminder.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
              {reminder.title}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClear(reminder.id);
              }}
              className="h-6 w-6 p-0 hover:bg-gray-200"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          
          <p className={`text-sm mt-1 ${!reminder.isRead ? 'text-gray-700' : 'text-gray-500'}`}>
            {reminder.message}
          </p>
          
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>{formatTime(reminder.createdAt)}</span>
            </div>
            
            {reminder.action && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClick}
                className="h-6 text-xs px-2"
              >
                {reminder.action.label}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {!reminder.isRead && (
        <div className="absolute top-2 right-8 w-2 h-2 bg-blue-500 rounded-full"></div>
      )}
    </div>
  );
};

export const NotificationDropdown: React.FC = () => {
  const { reminders, unreadCount, markAsRead, clearReminder, clearAllReminders } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Recordatorios del Sistema</span>
            {reminders.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearAllReminders();
                  setIsOpen(false);
                }}
                className="h-6 text-xs text-gray-500 hover:text-gray-700"
              >
                Limpiar todo
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-96 overflow-y-auto">
          {reminders.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <p>No hay recordatorios del sistema</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reminders.map((reminder) => (
                <NotificationItem
                  key={reminder.id}
                  reminder={reminder}
                  onMarkAsRead={markAsRead}
                  onClear={clearReminder}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};